import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAdminUsers, type AdminUser } from "../api/adminUsers";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";

interface UseAdminUserListOptions {
  page: number;
  pageSize: number;
  email?: string;
  showAll?: boolean;
  enabled?: boolean;
}

export const useAdminUserList = ({
  page,
  pageSize,
  email,
  showAll = false,
  enabled = true,
}: UseAdminUserListOptions) => {
  const [data, setData] = useState<AdminUser[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const listParamsRef = useRef({ page, pageSize, email, showAll });
  const hasLoadedOnceRef = useRef(false);

  const refetch = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const updateUser = useCallback((userId: number, updater: (user: AdminUser) => AdminUser) => {
    setData((current) => current.map((user) => (user.id === userId ? updater(user) : user)));
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const paginationChanged =
      listParamsRef.current.page !== page ||
      listParamsRef.current.pageSize !== pageSize ||
      listParamsRef.current.email !== email ||
      listParamsRef.current.showAll !== showAll;
    listParamsRef.current = { page, pageSize, email, showAll };

    let cancelled = false;
    const showFullLoading = paginationChanged || !hasLoadedOnceRef.current;

    if (showFullLoading) {
      setLoading(true);
    }
    setError(null);

    fetchAdminUsers({ page, pageSize, email, showAll })
      .then((response) => {
        if (!cancelled) {
          setData(response.items);
          setTotalPages(Math.max(1, response.totalPages));
          setTotalElements(response.totalElements);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = getApiErrorMessage(err, "Не удалось загрузить пользователей");
          setError(message);
          setData([]);
          setTotalPages(1);
          setTotalElements(0);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          hasLoadedOnceRef.current = true;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, email, showAll, enabled, reloadToken]);

  return { data, totalPages, totalElements, loading, error, refetch, updateUser };
};
